import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 118 Backoffice Geofence Zone API
// (BackofficeGeofenceController/GeofenceZoneService). Same base/auth
// convention as ../abnormal/abnormalApi.js/../footsteps/footstepsApi.js -
// apiClient.get/post/patch with no `base` override, the same Bearer-session-
// authenticated backend every other backoffice controller uses. This file
// never calls a polygon/fake-location-scoring/face-recognition/SOS API (none
// of those exist anywhere in this codebase), and it never sends a real
// notification - every write here maps 1:1 to one of the seven real P118
// routes below. Circle zones only - there is no polygon field anywhere in
// this file.

const GEOFENCE_PATH = "/backoffice/geofence";

/**
 * P161: the two zone profiles this screen can create.
 *
 * A Circle is a place - a worksite gate, with a centre and a radius. A Country
 * is a whole country, evaluated against that country's real outline on the
 * server and covering every worker automatically, with no per-worker assignment.
 * The literals match ZoneTypes in the backend exactly; anything else is not a
 * profile this system has.
 */
export const ZONE_TYPE_CIRCLE = "Circle";
export const ZONE_TYPE_COUNTRY = "Country";

/**
 * The countries the API ships an outline for. Thailand is the only one today -
 * listed here rather than fetched because the API exposes no
 * countries-with-boundaries endpoint, and inventing one in the UI would offer
 * choices the server then rejects. When a second country's outline ships, it is
 * one line here and one file there.
 */
export const COUNTRY_ZONE_OPTIONS = [{ countryCode: "TH", label: "ประเทศไทย (Thailand)" }];

// Exactly the query param names BackofficeGeofenceController.ListZones
// declares, in the same order as the controller signature - anything else
// passed to listGeofenceZones is silently dropped rather than forwarded, so
// a typo'd key can never accidentally reach the backend as an unfiltered
// pass-through. Scope-isolated visibility always happens on the backend from
// the caller's own session, never from a value this file sends.
const ZONES_LIST_PARAM_KEYS = [
  "search",
  "zoneStatus",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "agentId",
  "employerId",
  "page",
  "pageSize",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listGeofenceZones(params = {}) {
  return apiClient.get(`${GEOFENCE_PATH}/zones${buildQuery(ZONES_LIST_PARAM_KEYS, params)}`);
}

// Detail fetch requires a real, non-empty zoneId - this file never issues a
// request for a missing/blank id (the caller only calls this once a
// row/card is actually clicked).
export function getGeofenceZoneDetail(zoneId) {
  if (!zoneId) {
    throw new Error("getGeofenceZoneDetail requires a zoneId");
  }
  return apiClient.get(`${GEOFENCE_PATH}/zones/${encodeURIComponent(zoneId)}`);
}

// Circle geometry only - matches CreateZoneRequest's own field set exactly
// (Back/WorkmateX/API/RootAPI/Models/Backoffice/GeofenceDtos.cs). There is no
// polygon/vertex field here, ever.
export function createGeofenceZone({ zoneName, zoneType, countryCode, employerId, centerLatitude, centerLongitude, radiusMeters, note } = {}) {
  // P161: a Country-profile zone is the same endpoint with a different shape.
  // It carries no employer, no centre and no radius - it is a country, and
  // sending invented coordinates for one would be sending the server numbers it
  // will ignore while implying this screen measured something.
  if (zoneType === ZONE_TYPE_COUNTRY) {
    return apiClient.post(`${GEOFENCE_PATH}/zones`, {
      zoneName: zoneName || null,
      zoneType: ZONE_TYPE_COUNTRY,
      countryCode: countryCode || null,
      note: note || null,
    });
  }

  return apiClient.post(`${GEOFENCE_PATH}/zones`, {
    zoneName: zoneName || null,
    employerId: employerId || null,
    centerLatitude: centerLatitude === "" || centerLatitude == null ? null : Number(centerLatitude),
    centerLongitude: centerLongitude === "" || centerLongitude == null ? null : Number(centerLongitude),
    radiusMeters: radiusMeters === "" || radiusMeters == null ? null : Number(radiusMeters),
    note: note || null,
  });
}

// Every field is optional/omitted-when-blank - matches UpdateZoneRequest's
// own "left out of the request body leaves the column untouched" contract
// (see GeofenceDtos.cs doc comment on UpdateZoneRequest). JSON.stringify
// drops `undefined` keys entirely, which is what actually implements "omit".
export function updateGeofenceZone(zoneId, { zoneName, centerLatitude, centerLongitude, radiusMeters, zoneStatus, note } = {}) {
  if (!zoneId) {
    throw new Error("updateGeofenceZone requires a zoneId");
  }
  const body = {};
  if (zoneName) {
    body.zoneName = zoneName;
  }
  if (centerLatitude !== "" && centerLatitude != null) {
    body.centerLatitude = Number(centerLatitude);
  }
  if (centerLongitude !== "" && centerLongitude != null) {
    body.centerLongitude = Number(centerLongitude);
  }
  if (radiusMeters !== "" && radiusMeters != null) {
    body.radiusMeters = Number(radiusMeters);
  }
  if (zoneStatus) {
    body.zoneStatus = zoneStatus;
  }
  if (note !== undefined) {
    body.note = note || null;
  }
  return apiClient.patch(`${GEOFENCE_PATH}/zones/${encodeURIComponent(zoneId)}`, body);
}

export function assignWorkersToZone(zoneId, { workerIds, assignmentNote } = {}) {
  if (!zoneId) {
    throw new Error("assignWorkersToZone requires a zoneId");
  }
  return apiClient.post(`${GEOFENCE_PATH}/zones/${encodeURIComponent(zoneId)}/worker-assignments`, {
    workerIds: Array.isArray(workerIds) ? workerIds : [],
    assignmentNote: assignmentNote || null,
  });
}

export function removeWorkerZoneAssignment(zoneId, workerId) {
  if (!zoneId || !workerId) {
    throw new Error("removeWorkerZoneAssignment requires a zoneId and a workerId");
  }
  return apiClient.post(
    `${GEOFENCE_PATH}/zones/${encodeURIComponent(zoneId)}/worker-assignments/${encodeURIComponent(workerId)}/remove`,
    {}
  );
}

// dryRun defaults true, matching GeofenceEvaluateRequest.DryRun's own
// default (GeofenceDtos.cs) - a caller must explicitly opt out of dryRun to
// ever create a real OutOfZone abnormal event.
export function evaluateGeofence({ workerId, zoneId, dryRun = true } = {}) {
  const body = { dryRun: Boolean(dryRun) };
  if (workerId) {
    body.workerId = workerId;
  }
  if (zoneId) {
    body.zoneId = zoneId;
  }
  return apiClient.post(`${GEOFENCE_PATH}/evaluate`, body);
}
